import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useMemo } from 'react';
import ClientId from '~/constants/ClientId';
import { SectionName } from '~/constants/vdp/section';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { SebastianContext } from '~/context/common/SebastianContext';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import { useUserId } from '~/context/common/UserContext';
import { useIsDealershipPage } from '~/context/dealership/StaticContext';
import { useIsCampaignTemplateOne } from '~/context/vdp/StaticContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import fetchSebastianQuestions from '~/services/vdp/fetchSebastianQuestions';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetSebastianQuestions() {
  const {
    questions: { setData, setError, setLoading, setLoaded }
  } = useContext(SebastianContext);
  const eventCorrelationId = useEventCorrelationId();
  const vehicleDetails = useGetVehicleDetails();
  const userId = useUserId();
  const isCampaignTemplateOne = useIsCampaignTemplateOne();
  const isDealership = useIsDealershipPage();
  const trackEvent = useTrackEvent();

  const request = useMemo(
    () => ({
      correlationId: eventCorrelationId,
      kbbVehicleId: vehicleDetails.kbbVehicleId ?? 0,
      userId,
      vehicleId: vehicleDetails.vehicleId ?? 0,
      make: vehicleDetails.make ?? '',
      model: vehicleDetails.parentModel ?? '',
      page: ClientId.VDP_UI,
      trim: vehicleDetails.trim ?? '',
      year: vehicleDetails.year ?? 0
    }),
    [
      eventCorrelationId,
      userId,
      vehicleDetails.kbbVehicleId,
      vehicleDetails.vehicleId,
      vehicleDetails.make,
      vehicleDetails.parentModel,
      vehicleDetails.trim,
      vehicleDetails.year
    ]
  );

  const shouldFetch = !isCampaignTemplateOne && !isDealership;

  useEffectWithController(
    controller => {
      if (!shouldFetch) return;

      setLoading();

      fetchSebastianQuestions(request, controller)
        .then(questionsData => {
          setData(questionsData);

          trackEvent(SectionName.SebastianQuestions, {
            action: 'loaded',
            hasQuestions: Boolean(questionsData.questions.length),
            hasErrored: false,
            questionsData: questionsData
          });
        })
        .catch(
          generateErrorLogger(
            'Get Sebastian questions',
            {},
            {
              callback: error => {
                trackEvent(SectionName.SebastianQuestions, {
                  action: 'loaded',
                  hasQuestions: false,
                  hasErrored: true
                });
                setError(error);
              },
              controller
            }
          )
        );
    },
    [request, setData, setError, setLoaded, setLoading, shouldFetch, trackEvent]
  );
}
